import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteWorkspaceUser, getWorkspace, getWorkspaceInvitations, updateWorkspace, updateWorkspaceUserRole, getWorkspaceUsers } from "./requests";
import type { CustomQueryOptions, Invitation, Pagination, User, Workspace } from "../types";


export const useGetWorkspaceQuery = (workspaceId: string, options?: CustomQueryOptions<Workspace>) => {
  return useQuery({
    queryKey: ['workspaces', workspaceId],
    queryFn: () => getWorkspace(workspaceId),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    ...options,
  });
};

export const useUpdateWorkspaceMutation = (workspaceId: string, onSuccess?: (data: any) => void, onError?: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => updateWorkspace(workspaceId, data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces', workspaceId] });
    },
  });
};

// Users
export const useGetWorkspaceUsers = (id: string, filter: { page: number, limit: number }, options?: CustomQueryOptions<{data: User[], pagination: Pagination}>) => {
  return useQuery({
    queryKey: ['workspace-users', id],
    queryFn: () => getWorkspaceUsers(id, filter),
    enabled: !!id,
    ...options
  });
};

export const useUpdateWorkspaceUserRole = (id: string, onSuccess?: () => void, onError?: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { userId: string; role: string }) => updateWorkspaceUserRole(id, data.userId, { role: data.role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-users', id] });
      onSuccess?.();
    },
    onError
  });
};

export const useDeleteWorkspaceUser = (id: string, onSuccess?: () => void, onError?: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => deleteWorkspaceUser(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-users', id] });
      onSuccess?.();
    },
    onError
  });
};

// Invitations
export const useGetWorkspaceInvitations = (id: string, filter: { page: number, limit: number }, options?: CustomQueryOptions<{data: Invitation[], pagination: Pagination}>) => {
  return useQuery({
    queryKey: ['workspace-invitations', id],
    queryFn: () => getWorkspaceInvitations(id, filter),
    enabled: !!id,
    ...options
  });
};